/**
 * pose-reset monitor (issue #286 / ADR 0013) — 頭部 yaw の「大変化」 を検出し dynamic-open-baseline の
 * 窓 reset trigger を返す pure helper。
 *
 * 機序 (ADR 0013): 正面閉口で形成された baseline p10 は pose 非依存の時間窓のため、 頭部旋回後も最大
 * `(1−p10%)×baselineWindowMs ≈ 7.2s` 正面値のまま残り、 yaw ~40° の閉口ホールドで effectiveMar に
 * +0.3 級の下駄を履かせて誤発火する (= 遷移 idle-oblique FP)。 pose 大変化を trigger に窓を破棄すれば
 * 旋回後は斜め姿勢の MAR 分布で baseline が再形成され、 下駄が消える。
 *
 * trigger は 2 条件の併用 (= AND、 本命 variant `zone+d10`、 issue #286 で事前登録):
 *   - zone crossing: |yaw| が 25/35° ヒステリシス境界を横断 (frontal→oblique は enter 35°、
 *     oblique→frontal は exit 25°)。 ヒステリシス幅 10° は境界付近の往復で連射しないため
 *   - delta: 直近 `deltaWindowMs` (500ms) 内の |Δyaw| が `deltaDeg` (10°) を超過。
 *     正面静止のノイズ floor は 3 軸 ~10° の range
 *     (docs/solutions/architecture-patterns/headpose-sign-convention-verification-2026-05-24.md) のため
 *     閾値は floor 直上 — 500ms の短窓で「速い旋回」 だけを拾い、 緩慢ドリフトは窓 evict で拾わない
 *
 * AND の理由 (F51 eval evidence): zone crossing 単独 (OR) は TP-oblique trial 中の境界往復で spurious
 * reset を起こし retention を 19→17 に退行させた。 「速い境界横断のみ」 (zone AND delta) に絞ると
 * trigger 集合は delta 単独 (`reset-yaw-10` フォールバック) の部分集合になり、 退行が消える。
 * zoneEnabled=false は delta 単独 = trigger がより多い側の variant。
 *
 * |yaw| / |Δyaw| の絶対値設計により deriveHeadPose の符号規約 (未確定、 同 doc) に依存しない。
 *
 * trigger 後は `rearmMs` (= baseline の cold-start 1s と同値) の debounce を置く — cold-start 中の
 * 再 reset は無害だが、 旋回継続中に delta が連射して cold-start が無限延長されるのを防ぐ。
 *
 * eval substrate 側の twin は `features/eval/lib/transforms/dynamic-open-baseline-v1.ts` の
 * `poseReset` params 群 (独立実装、 #236 KTD1)。 定数を変えるときは twin の default と
 * `dispatcher.test.ts` の parity を同時に確認すること。
 *
 * monitor 自体は signal-agnostic (push に渡す角度列に対する pure な trigger 判定)。 production は
 * 2 instance を使う (ADR 0013 §Update 2026-06-12 / F52 lever A): yaw = default (zone+d10)、
 * pitch = `{ zoneEnabled: false }` (delta 単独 — 自然範囲の pitch 上げ ~24-32° は zone enter 35° に
 * 届かず zone+d10 が inert になるため)。 detection-loop が両者を OR で baseline reset に繋ぐ。
 */

/** frontal→oblique の zone 進入境界 (|yaw| 度)。 twin / test から参照するため export。 */
export const DEFAULT_ZONE_ENTER_DEG = 35;
/** oblique→frontal の zone 退出境界 (|yaw| 度)。 enter より 10° 低いヒステレシス。 */
export const DEFAULT_ZONE_EXIT_DEG = 25;
/** delta trigger の |Δyaw| 閾値 (度)。 正面静止ノイズ floor (~10°) 直上。 */
export const DEFAULT_DELTA_DEG = 10;
/** delta trigger の比較窓 (ms)。 「速い旋回」 だけを拾う短窓。 */
export const DEFAULT_DELTA_WINDOW_MS = 500;
/** trigger 後の debounce (ms)。 baseline cold-start (DEFAULT_COLD_START_MS=1000) と同値。 */
export const DEFAULT_REARM_MS = 1_000;

export type PoseResetMonitorOptions = {
  /** false = delta 単独 trigger (= フォールバック variant `reset-yaw-10`、 issue #286 事前登録)。 */
  zoneEnabled?: boolean;
  zoneEnterDeg?: number;
  zoneExitDeg?: number;
  deltaDeg?: number;
  deltaWindowMs?: number;
  rearmMs?: number;
};

export type PoseResetMonitor = {
  /**
   * frame ごとに呼ぶ。 yaw (度、 headPose 由来) が undefined / non-finite (= face-loss) の frame は
   * 状態を保持したまま skip して false。 trigger 成立かつ debounce 解放済のとき true を 1 回返す
   * (= caller が dynamic-open-baseline の reset を呼ぶ)。
   */
  push: (yaw: number | undefined, timestamp: number) => boolean;
};

type YawSample = { yaw: number; t: number };

export function createPoseResetMonitor(opts: PoseResetMonitorOptions = {}): PoseResetMonitor {
  const zoneEnabled = opts.zoneEnabled ?? true;
  const zoneEnterDeg = opts.zoneEnterDeg ?? DEFAULT_ZONE_ENTER_DEG;
  const zoneExitDeg = opts.zoneExitDeg ?? DEFAULT_ZONE_EXIT_DEG;
  const deltaDeg = opts.deltaDeg ?? DEFAULT_DELTA_DEG;
  const deltaWindowMs = opts.deltaWindowMs ?? DEFAULT_DELTA_WINDOW_MS;
  const rearmMs = opts.rearmMs ?? DEFAULT_REARM_MS;

  // zone は最初の有効 yaw で trigger なしに初期化する (= セッション開始が既に斜めでも spurious reset
  // しない。 baseline 自体が斜め分布で形成されるので reset 不要)。
  let zone: "frontal" | "oblique" | null = null;
  const yawHistory: YawSample[] = [];
  let lastResetAt: number | null = null;

  return {
    push: (yaw, timestamp) => {
      if (typeof yaw !== "number" || !Number.isFinite(yaw)) return false;
      if (!Number.isFinite(timestamp)) return false;
      const absYaw = Math.abs(yaw);

      let zoneCrossed = false;
      if (zone === null) {
        zone = absYaw >= zoneEnterDeg ? "oblique" : "frontal";
      } else if (zone === "frontal" && absYaw >= zoneEnterDeg) {
        zone = "oblique";
        zoneCrossed = true;
      } else if (zone === "oblique" && absYaw <= zoneExitDeg) {
        zone = "frontal";
        zoneCrossed = true;
      }

      // delta: 窓内 (deltaWindowMs) の過去 yaw との最大差。 現 sample を比較後に積む。
      const cutoff = timestamp - deltaWindowMs;
      while (yawHistory.length > 0) {
        const head = yawHistory[0];
        if (head === undefined || head.t >= cutoff) break;
        yawHistory.shift();
      }
      const deltaExceeded = yawHistory.some((s) => Math.abs(yaw - s.yaw) > deltaDeg);
      yawHistory.push({ yaw, t: timestamp });

      // zone+d10 (zoneEnabled=true) = 「速い境界横断」 のみ (AND)。 delta 単独 (false) = 速い旋回全般。
      const triggered = zoneEnabled ? zoneCrossed && deltaExceeded : deltaExceeded;
      if (!triggered) return false;
      // debounce: trigger 成立でも rearmMs 以内の再発は reset しない。 zone / delta の状態追跡は
      // 上で更新済 (= debounce 中も姿勢追従は継続する)。
      if (lastResetAt !== null && timestamp - lastResetAt < rearmMs) return false;
      lastResetAt = timestamp;
      return true;
    },
  };
}
